import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard } from '../platform/_ui';
import * as api from '../api/org-billing.api.js';
import { usePermissions } from './auth/usePermissions.js';
import { ORG } from './auth/capabilities.js';
import { DeniedState } from './auth/DeniedState.jsx';

const fmt    = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtAmt = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const fmtEnum = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ') : '—';

const STATUS_TONE = { PAID: 'green', OPEN: 'amber', OVERDUE: 'red', VOID: 'gray' };
const LIC_TYPE_LABEL = { USER: 'User Seats', AGENT: 'Agent Seats', BOT: 'Bot Seats', WORKSPACE: 'Workspace', STORAGE: 'Storage (GB)', API: 'API Calls / day', MESSAGE: 'Messages / month', CUSTOM: 'Custom' };

export default function OrgBilling({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const toast      = useToast();

  const perm        = usePermissions();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';
  const [billing, setBilling]   = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);

  const org = data.state.organizations.find(o => o.id === orgId);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [b, inv] = await Promise.all([
        api.getBilling(orgId),
        api.getInvoices(orgId).catch(() => []),
      ]);
      setBilling(b);
      setInvoices(Array.isArray(inv) ? inv : inv?.invoices || []);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load billing data' });
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  if (!org) return null;

  if (!perm.has(ORG.BILLING_VIEW) && !perm.isSA) return (
    <div className="nx-cc-page">
      <DeniedState reason="insufficient_permission" message="Plan & Billing requires the Billing View capability. Contact your org admin." />
    </div>
  );

  const used      = billing?.license?.used ?? 0;
  const allotted  = billing?.license?.allotted ?? 0;
  const available = allotted - used;
  const usePct    = allotted > 0 ? Math.round((used / allotted) * 100) : 0;

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Plan & Billing"
        sub={`Subscription, licenses, and payment history for ${org.name}`}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
      />

      {loading ? (
        <div className="nx-empty" style={{ padding: '48px 0' }}><NexusIcon name="Loader" size={20} /><span>Loading billing data…</span></div>
      ) : !billing ? (
        <div className="nx-empty" style={{ padding: '48px 0' }}><NexusIcon name="AlertCircle" size={20} /><span>Could not load billing data.</span></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* ── Quick stats ── */}
          <div className="nx-cc-stat-grid nx-kpi-4">
            <StatCard icon="Tag"       label="Current Plan"    value={billing.plan || org.plan || 'No Plan'} color="blue" />
            <StatCard icon="Users"     label="Seats Purchased" value={allotted > 0 ? String(allotted) : '—'} sub={allotted > 0 ? `${used} in use · ${Math.max(0, available)} free` : 'No license'} color="blue"  />
            <StatCard icon="Activity"  label="Seats In Use"    value={String(used)} sub={allotted > 0 ? `${usePct}% utilization` : '—'} color={usePct >= 90 ? 'red' : usePct >= 70 ? 'amber' : 'green'} />
            <StatCard icon="Calendar"  label="Next Renewal"    value={fmt(billing.license?.renews)} sub={billing.license?.type ? LIC_TYPE_LABEL[billing.license.type] || billing.license.type : '—'} color="purple" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* ── Current Plan ── */}
            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Current Plan</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nx-text)', marginBottom: 4 }}>{billing.plan || org.plan || '—'}</div>
              {org.subscriptionStatus && (
                <Pill tone={org.subscriptionStatus === 'ACTIVE' ? 'green' : org.subscriptionStatus === 'TRIAL' ? 'blue' : org.subscriptionStatus === 'SUSPENDED' ? 'amber' : 'gray'} style={{ marginBottom: 12 }}>
                  {org.subscriptionStatus}
                </Pill>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginTop: 10 }}>
                {[
                  { label: 'Billing Cycle',  value: org.billingCycle ? (org.billingCycle.charAt(0) + org.billingCycle.slice(1).toLowerCase()) : '—' },
                  { label: 'Start Date',     value: fmt(org.startsAt) },
                  { label: 'Renewal Date',   value: fmt(org.renewalAt || billing.license?.renews) },
                  { label: 'Auto-Renew',     value: org.autoRenew != null ? (org.autoRenew ? 'Enabled' : 'Disabled') : '—' },
                  { label: 'Contract Ref',   value: org.contractRef || '—' },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--nx-divider)' }}>
                    <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{row.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', fontFamily: row.label === 'Contract Ref' ? 'monospace' : undefined }}>{row.value}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10 }}>
                <p style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
                  To change your plan or billing terms, contact your platform administrator.
                </p>
              </div>
            </div>

            {/* ── License summary ── */}
            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>License Summary</div>
              {!billing.license ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 0', gap: 8 }}>
                  <NexusIcon name="Key" size={20} style={{ color: 'var(--nx-text-faint)' }} />
                  <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>No license on record</div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-faint)', textAlign: 'center', maxWidth: 200 }}>Contact your platform administrator to assign seat licenses.</div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
                    {[
                      { label: 'Purchased', value: allotted, color: 'var(--nx-accent, #125AB0)' },
                      { label: 'In Use',    value: used,     color: 'var(--nx-accent)' },
                      { label: 'Available', value: Math.max(0, available), color: available <= 0 ? 'var(--nx-danger)' : 'var(--nx-accent)' },
                    ].map(s => (
                      <div key={s.label} style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 8, background: 'var(--nx-bg-alt, rgba(0,0,0,0.03))', border: '1px solid var(--nx-border)' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: s.color, fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  {/* Usage bar */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                      <span style={{ color: 'var(--nx-text-muted)' }}>Utilization</span>
                      <span style={{ fontWeight: 700, color: usePct >= 90 ? 'var(--nx-danger)' : usePct >= 70 ? 'var(--nx-warning)' : 'var(--nx-text)' }}>{usePct}%</span>
                    </div>
                    <div style={{ height: 8, background: 'var(--nx-border)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${usePct}%`, background: usePct >= 90 ? 'var(--nx-danger)' : usePct >= 70 ? 'var(--nx-warning)' : 'var(--nx-success)', borderRadius: 4, transition: 'width 0.4s' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                    Type: <strong style={{ color: 'var(--nx-text)' }}>{LIC_TYPE_LABEL[billing.license.type] || billing.license.type || '—'}</strong>
                    {billing.license.renews && (
                      <> · Renews <strong style={{ color: 'var(--nx-text)' }}>{fmt(billing.license.renews)}</strong></>
                    )}
                  </div>
                </>
              )}
              <div style={{ marginTop: 12 }}>
                <p style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
                  To purchase more seats, contact your platform administrator.
                </p>
              </div>
            </div>
          </div>

          {/* ── Transaction history ── */}
          <div className="nx-cc-chart-card" style={{ padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>License History</div>
            {(billing.history?.length ?? 0) === 0 ? (
              <div className="nx-empty" style={{ padding: '24px 0' }}><NexusIcon name="FileText" size={20} /><span>No license history yet.</span></div>
            ) : (
              <table className="nx-table">
                <thead><tr><th>Date</th><th>Action</th><th>Seats</th><th>Note</th><th>By</th></tr></thead>
                <tbody>
                  {billing.history.map(h => (
                    <tr key={h.id}>
                      <td style={{ fontSize: 12 }}>{fmt(h.createdAt)}</td>
                      <td><Pill tone="gray">{fmtEnum(h.action)}</Pill></td>
                      <td style={{ fontWeight: 600 }}>{h.seats ?? '—'}</td>
                      <td style={{ color: 'var(--nx-text-muted)', fontSize: 12 }}>{h.note ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{h.actor ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Invoices ── */}
          <div className="nx-cc-chart-card" style={{ padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Invoices</div>
            {invoices.length === 0 ? (
              <div className="nx-empty" style={{ padding: '24px 0' }}><NexusIcon name="Receipt" size={20} /><span>No invoices available. Invoices are issued by your Nuacem account manager.</span></div>
            ) : (
              <table className="nx-table">
                <thead><tr><th>Invoice Date</th><th>Amount</th><th>Status</th><th style={{ textAlign: 'right' }}>Download</th></tr></thead>
                <tbody>
                  {invoices.map((inv, i) => {
                    const status = (inv.status || '').toUpperCase();
                    return (
                      <tr key={inv.id || i}>
                        <td style={{ fontSize: 12 }}>{fmt(inv.date || inv.createdAt)}</td>
                        <td style={{ fontWeight: 700, fontSize: 14 }}>{fmtAmt(inv.amount != null ? inv.amount : inv.total)}</td>
                        <td><Pill tone={STATUS_TONE[status] || 'gray'}>{fmtEnum(inv.status) || 'Due'}</Pill></td>
                        <td style={{ textAlign: 'right' }}>
                          {inv.downloadUrl
                            ? <a href={inv.downloadUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--nx-accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><NexusIcon name="Download" size={12} />PDF</a>
                            : <span style={{ fontSize: 12, color: 'var(--nx-text-faint)' }}>—</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
